import React from 'react'
import { Location, Navigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from './store'
import { showBanner } from './global-state/banner-slice'
import { getInvestorDataFromSS } from './lib/utils/local-storage'
import {
  resolvePostAuthCampaignDestination,
  storeOpportunityRedirectFromLocation,
} from './lib/utils/campaign-signup-redirect'
import {
  isSuperuserSession,
  POST_ONBOARDING_PATH,
  resolveOnboardingPathFromFlags,
  resolveRiskProfilerRequired,
  shouldPromptConfidentialityAgreement,
} from './lib/utils/onboarding'

const normalizePathname = (pathname: string): string =>
  pathname.endsWith('/') && pathname.length > 1 ? pathname.slice(0, -1) : pathname

const ONBOARDING_BASIC_DETAILS_PATH = '/onboarding/basic-details'
const ONBOARDING_RISK_PROFILER_PATH = '/onboarding/risk-profiler'

const isOnboardingRoute = (pathname: string): boolean => pathname.startsWith('/onboarding')

const isAllowedOnboardingRoute = (
  pathname: string,
  basicDetailsCompleted: boolean,
  riskProfilerRequired: boolean,
): boolean => {
  if (pathname === ONBOARDING_BASIC_DETAILS_PATH) {
    return !basicDetailsCompleted
  }

  if (pathname === ONBOARDING_RISK_PROFILER_PATH) {
    return basicDetailsCompleted && riskProfilerRequired
  }

  return false
}

// '/verify-email' is the magic-link landing page; an unverified, signed-in investor must reach it.
const EMAIL_VERIFICATION_ROUTES = new Set(['/verify-account', '/welcome', '/verify-email'])

/** Auth pages perform their own post-login navigation (including campaign redirects). */
const isAuthEntryRoute = (pathname: string): boolean =>
  pathname === '/login' || pathname === '/signup' || pathname.startsWith('/signup/')
interface PrivateRouteProps {
  perms?: string[]
  children: React.ReactNode
}
interface PublicRouteProps {
  children: React.ReactNode
}
interface CanIUseProps {
  perms?: string[]
  isSelfDeclarationSigned: boolean
  isRiskProfileCompleted: boolean
  isKycApproved: boolean
  isEmailVerified: boolean
  isAccountDormant: boolean
  location: Location
  children: React.ReactNode
}
export const KYC = 'kyc'
export const NO_NDA = 'NotNDA'
export const NDA = 'NDA'
export const EmailVerified = 'EmailVerified'
export const AccountNotDormant = 'AccountNotDormant'
const permissions = {
  KYC: 'kyc',
  NDA: 'NDA',
  NotNDA: 'NotNDA',
  EmailVerified: 'EmailVerified',
  AccountNotDormant: 'AccountNotDormant',
}
//these values should be part of the investor configuration in global state and should be fetched from there

const PrivateRoute = ({ perms, children }: PrivateRouteProps) => {
  const location = useLocation()
  const { authToken } = getInvestorDataFromSS()

  const {
    acceptedNDA,
    kycStatus,
    isEmailVerified,
    accountStatus,
    basicDetailsCompleted,
    riskProfileCompleted,
    riskProfilerRequired,
    adminRole,
  } = useSelector((state: RootState) => state.investor)
  const isLoggedIn = !!authToken
  const isSelfDeclarationSigned = acceptedNDA
  const isKycApproved = kycStatus === 'APPROVED' ? true : false
  const isAccountDormant = accountStatus === 'DORMANT' ? true : false
  if (!isLoggedIn) {
    storeOpportunityRedirectFromLocation(location.pathname, location.search)
    return <Navigate to='/login' replace />
  }

  const normalizedPath = normalizePathname(location.pathname)
  const isSuperuser = isSuperuserSession(adminRole)

  if (isSuperuser && isOnboardingRoute(normalizedPath)) {
    return <Navigate to={POST_ONBOARDING_PATH} replace />
  }

  const profilerRequired = resolveRiskProfilerRequired({
    riskProfilerRequired,
    riskProfileCompleted,
  })

  const requiredOnboardingPath = resolveOnboardingPathFromFlags(
    basicDetailsCompleted,
    profilerRequired,
    isSuperuser,
  )

  if (requiredOnboardingPath.startsWith('/onboarding')) {
    if (
      !isOnboardingRoute(normalizedPath) ||
      !isAllowedOnboardingRoute(normalizedPath, basicDetailsCompleted, profilerRequired)
    ) {
      storeOpportunityRedirectFromLocation(location.pathname, location.search)
      return <Navigate to={requiredOnboardingPath} replace />
    }
  }

  return canIUse({
    perms,
    isSelfDeclarationSigned,
    isRiskProfileCompleted: riskProfileCompleted,
    isKycApproved,
    isEmailVerified,
    children,
    isAccountDormant,
    location,
  })
}

const PublicRoute = ({ children }: PublicRouteProps) => {
  const location = useLocation()
  const authTokenFromState = useSelector((state: RootState) => state.investor.authToken)
  const acceptedNDA = useSelector((state: RootState) => state.investor.acceptedNDA)
  const isEmailVerified = useSelector((state: RootState) => state.investor.isEmailVerified)
  const basicDetailsCompleted = useSelector(
    (state: RootState) => state.investor.basicDetailsCompleted,
  )
  const riskProfileCompleted = useSelector(
    (state: RootState) => state.investor.riskProfileCompleted,
  )
  const riskProfilerRequired = useSelector(
    (state: RootState) => state.investor.riskProfilerRequired,
  )
  const adminRole = useSelector((state: RootState) => state.investor.adminRole)
  const { authToken: authTokenFromSession } = getInvestorDataFromSS()
  const isLoggedIn = Boolean(authTokenFromState || authTokenFromSession)
  if (!isLoggedIn) return <>{children}</>

  const normalizedPath = normalizePathname(location.pathname)
  if (!isEmailVerified && EMAIL_VERIFICATION_ROUTES.has(normalizedPath)) {
    return <>{children}</>
  }

  // The magic-link page signs the investor in (so they become "verified" mid-render) and then
  // navigates itself, e.g. to /set-password; redirecting here would override that.
  if (normalizedPath === '/verify-email') {
    return <>{children}</>
  }

  if (isAuthEntryRoute(normalizedPath)) {
    return <>{children}</>
  }

  const postLoginPath = resolveOnboardingPathFromFlags(
    basicDetailsCompleted,
    resolveRiskProfilerRequired({ riskProfilerRequired, riskProfileCompleted }),
    isSuperuserSession(adminRole),
  )
  if (postLoginPath.startsWith('/onboarding')) {
    return <Navigate to={postLoginPath} replace />
  }

  const campaignDestination = resolvePostAuthCampaignDestination(acceptedNDA)
  if (campaignDestination) {
    return <Navigate to={campaignDestination} replace />
  }

  return <Navigate to={postLoginPath} replace />
}

const canIUse = ({
  perms,
  isSelfDeclarationSigned,
  isRiskProfileCompleted,
  isKycApproved,
  isEmailVerified,
  children,
  isAccountDormant,
  location,
}: CanIUseProps) => {
  const dispatch = useDispatch()
  if (perms && perms?.includes(permissions['KYC'])) {
    if (!isKycApproved) {
      dispatch(
        showBanner({
          text: 'Please complete KYC process to proceed',
          variant: 'warning-banner',
        }),
      )
      return <Navigate to='/profile/kyc' replace />
    }
  }

  if (perms && perms?.includes(permissions['EmailVerified'])) {
    if (!isEmailVerified) {
      return <Navigate to='/opportunities' replace />
    }
  }
  if (perms && perms?.includes(permissions['NDA'])) {
    if (shouldPromptConfidentialityAgreement(isRiskProfileCompleted, isSelfDeclarationSigned)) {
      storeOpportunityRedirectFromLocation(location.pathname, location.search)
      return <Navigate to='/accept-NDA' replace />
    }
  }
  if (perms && perms?.includes(permissions['NotNDA'])) {
    if (isSelfDeclarationSigned) return <></>
  }

  if (perms && perms?.includes(permissions['AccountNotDormant'])) {
    if (isAccountDormant) {
      dispatch(
        showBanner({
          text: 'Your account is dormant. Please contact support if you have not already.',
          variant: 'warning-banner',
          duration: 10000,
        }),
      )
      return <Navigate to='/contact-us' replace />
    }
  }
  return <>{children}</>
}
export { PrivateRoute, PublicRoute }
